import { useEffect, useRef, useState } from 'react';
import { Link, NavLink } from 'react-router';
import { userAvatarFallback } from '@/utils/userAvatar';
import { useMeUser } from '@/hooks/useUser.tsx';
import { ThemeToggle } from '@/components/atoms/ThemeToggle';
import logoUrl from '@/assets/logo.svg';
import {
  Home,
  Swords,
  Map,
  PlayCircle,
  Shield,
  Award,
} from 'lucide-react';

declare const __APP_VERSION__: string;

const navItems = [
  { to: '/home', label: 'Accueil', icon: Home },
  { to: '/pokemons', label: 'Pokémons', icon: Swords },
  { to: '/map', label: 'Carte', icon: Map },
  { to: '/achievements', label: 'Succès', icon: Award },
  { to: '/videos', label: 'Vidéos', icon: PlayCircle },
  { to: '/role', label: 'Rôles', icon: Shield },
];

const baseLinkClass =
  'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors duration-200';

export const Navbar = () => {
  const { meUser } = useMeUser();
  const appVersion = __APP_VERSION__;
  const [hasAvatarError, setHasAvatarError] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const headerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY.current;
      const headerHeight = headerRef.current?.offsetHeight ?? 0;
      if (Math.abs(delta) < 4) return;
      if (currentScrollY <= headerHeight) {
        setIsVisible(true);
      } else if (delta > 0) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const avatarBlock = () => (
    <div className="bg-primary-500/20 flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full">
      {meUser?.avatarUrl && !hasAvatarError ? (
        <img
          src={meUser.avatarUrl}
          alt={`Avatar de ${meUser.username}`}
          className="h-full w-full object-cover"
          onError={() => setHasAvatarError(true)}
        />
      ) : (
        <span className="text-content font-lexend text-[10px] font-bold uppercase">
          {meUser ? userAvatarFallback(meUser.username) : '?'}
        </span>
      )}
    </div>
  );

  return (
    <header
      ref={headerRef}
      className={`bg-warm border-b border-line-soft sticky top-0 z-50 shadow-sm transition-transform duration-300 will-change-transform ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="flex items-stretch">
        {/* Logo */}
        <div className="relative flex w-24 shrink-0 items-center bg-[linear-gradient(147deg,rgba(57,87,106,1)_4%,rgba(239,186,43,1)_48%,rgba(109,20,23,1)_100%)] px-4 py-3 sm:w-28 sm:px-5">
          <img src={logoUrl} alt="Logo Bitume" className="h-8 sm:h-10" />
          <span className="pointer-events-none absolute bottom-0.5 left-1 text-[8px] leading-none text-white/50">
            v{appVersion}
          </span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden flex-1 items-center justify-between px-4 py-2 lg:flex xl:px-6">
          <div className="flex items-center gap-0.5 xl:gap-1">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/pokemons' ? false : undefined}
                className={({ isActive }) =>
                  `${baseLinkClass} ${
                    isActive
                      ? 'bg-accent text-white'
                      : 'text-soft hover:bg-raised hover:text-content'
                  }`
                }
              >
                <Icon size={14} />
                {label}
              </NavLink>
            ))}
          </div>

          <div className="ml-3 flex shrink-0 items-center gap-2">
            <ThemeToggle />
            {meUser && (
              <Link
                to="/profile"
                className="flex items-center gap-2 rounded-full bg-raised py-1 pr-3 pl-1.5 transition-colors hover:bg-line-soft"
              >
                {avatarBlock()}
                <span className="text-content text-sm font-semibold">
                  {meUser.username}
                </span>
              </Link>
            )}
          </div>
        </nav>

        {/* Mobile : theme toggle + avatar (nav via BottomNav) */}
        <div className="flex flex-1 items-center justify-end gap-1 px-3 py-2 lg:hidden">
          <ThemeToggle />
          {meUser && (
            <Link
              to="/profile"
              className="flex items-center gap-2 rounded-full bg-raised py-1 pr-3 pl-1.5 transition-colors hover:bg-line-soft"
            >
              {avatarBlock()}
              <span className="hidden text-sm font-semibold text-content sm:inline">
                {meUser.username}
              </span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
};
